import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CalendarBlank, Clock, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";
import { useNavigation } from "../context/NavigationContext";
import { getArticleBySlug, getSlugForLang } from "../data/articles";
import { sanitizeHtml } from "../utils/sanitize";
import { buildLangPath, SUPPORTED_LANGS } from "../utils/langPath";
import ShareButtons from "../components/ShareButtons";
import RelatedArticles from "../components/RelatedArticles";
import SEOHead from "../components/SEOHead";

const SITE_URL = "https://luminawallet.org";

const BlogPostPage = () => {
  const { t, i18n } = useTranslation();
  const { blogSlug, navigateTo } = useNavigation();
  const article = getArticleBySlug(blogSlug, i18n.language);

  const articleHtml = useMemo(
    () => ({ __html: sanitizeHtml(article?.bodyHtml ?? "") }),
    [article?.bodyHtml]
  );

  // Compute hreflang path map per language so SEOHead generates correct
  // <link rel="alternate" hreflang="…"> for the current article.
  // Only emit alternates for languages that actually have translated content
  // for this article (avoids exposing legacy or missing versions in SEO).
  const pathByLang = useMemo(() => {
    if (!article) return null;
    const available = new Set(article.availableLangs || SUPPORTED_LANGS);
    const map = {};
    SUPPORTED_LANGS.forEach((lang) => {
      if (available.has(lang)) {
        map[lang] = `/blog/${getSlugForLang(article.canonicalSlug, lang)}`;
      }
    });
    return map;
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen pt-28 pb-20 flex items-center justify-center" data-testid="blog-post-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">{t("blog.notFound")}</h1>
          <button
            onClick={() => navigateTo("blog")}
            className="text-lumina-violet hover:underline"
            data-testid="back-to-blog-btn"
          >
            {t("blog.backToBlog")}
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="blog-post-page">
      <SEOHead
        title={`${article.title} | Lumina Wallet`}
        description={article.meta}
        path={`/blog/${article.slug}`}
        pathByLang={pathByLang}
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <button
          onClick={() => navigateTo("blog")}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          data-testid="blog-back-btn"
        >
          <ArrowLeft size={16} weight="bold" />
          {t("blog.backToBlog")}
        </button>

        <div className="rounded-2xl overflow-hidden mb-8">
          <img
            src={article.image}
            alt={article.title}
            className="w-full aspect-[2/1] object-cover"
            width="800"
            height="400"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
        </div>

        <div className="flex items-center gap-4 mb-6 text-sm text-gray-400">
          <div className="flex items-center gap-1.5">
            <CalendarBlank size={16} weight="regular" />
            <span>{formatDate(article.date)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock size={16} weight="regular" />
            <span>{t("blog.readTimeFormat", { min: article.readTime })}</span>
          </div>
        </div>

        <h1
          className="text-3xl sm:text-4xl font-bold text-white mb-8 leading-tight"
          data-testid="blog-post-title"
        >
          {article.title}
        </h1>

        <div
          className="blog-content prose prose-invert max-w-none"
          // Article body sanitized upstream via `sanitizeHtml` (DOMPurify strict allowlist).
          // See /app/frontend/src/utils/sanitize.js for the mitigation.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={articleHtml}
          data-testid="blog-post-content"
        />

        <ShareButtons title={article.title} url={`${SITE_URL}${buildLangPath(i18n.language, `/blog/${article.slug}`)}`} />

        <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-lumina-violet/20 to-purple-900/20 border border-lumina-violet/20 text-center">
          <h3 className="text-xl font-bold text-white mb-3">
            {t("blog.cta.title")}
          </h3>
          <p className="text-gray-300 mb-6 max-w-lg mx-auto">
            {t("blog.cta.description")}
          </p>
          <button
            onClick={() => navigateTo("download")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-lumina-violet to-purple-600 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-lumina-violet/25 transition-all"
            data-testid="blog-cta-download"
          >
            <DownloadSimple size={20} weight="bold" />
            {t("blog.cta.button")}
          </button>
        </div>

        <RelatedArticles currentSlug={article.slug} />
      </article>
    </div>
  );
};

export default BlogPostPage;
